import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../../services/apiClient";
import "../anounce/anounce.css";
import DateInput from "../../components/site/DateInput";
import { formatDate, formatDateTime } from "../../utils/dateFormat";
import { PageNav, PageSummary, usePaging } from "../../components/admin/Pager";

const toAbsoluteUrl = (url) => {
  if (!url) return "";
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  return `${API_BASE}${url}`;
};

function AdminDailyAnnouncementManager() {
  // Bangkok calendar day (UTC would open on yesterday between 00:00 and 06:59)
  const todayStr = useMemo(() => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10), []);
  const [manageDate, setManageDate] = useState(todayStr);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsMsg, setItemsMsg] = useState("");
  const [editMap, setEditMap] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfMsg, setPdfMsg] = useState("");
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState("");
  const [pdfList, setPdfList] = useState([]);
  const itemPaging = usePaging(items, "mng_daily_items_page_size", manageDate);
  const pdfPaging = usePaging(pdfList, "mng_daily_pdfs_page_size", "");
  const [pdfListLoading, setPdfListLoading] = useState(false);
  const [pdfListMsg, setPdfListMsg] = useState("");

  const loadAnnouncementsByDate = async (date) => {
    if (!date) return;

    try {
      setItemsLoading(true);
      setItemsMsg("");
      setGeneratedPdfUrl("");
      setPdfMsg("");

      const res = await fetch(`${API_BASE}/api/announcements/by-date?date=${encodeURIComponent(date)}`);
      const data = await res.json();

      if (!res.ok || !data.success) {
        setItems([]);
        setItemsMsg(data.message || "โหลดรายการไม่สำเร็จ");
        return;
      }

      const nextItems = (data.items || []).map((item) => ({
        ...item,
        imageUrl: toAbsoluteUrl(item.imageUrl),
      }));
      setItems(nextItems);

      const nextEdit = {};
      nextItems.forEach((item) => {
        nextEdit[item.id] = item.issueDate || "";
      });
      setEditMap(nextEdit);

      if (nextItems.length === 0) {
        setItemsMsg("ไม่พบประกาศในวันที่เลือก");
      }
    } catch (err) {
      console.error(err);
      setItems([]);
      setItemsMsg("เชื่อมต่อ server ไม่ได้");
    } finally {
      setItemsLoading(false);
    }
  };

  const loadPdfList = async () => {
    try {
      setPdfListLoading(true);
      setPdfListMsg("");

      const res = await fetch(`${API_BASE}/api/daily-pdf/list`);
      const data = await res.json();

      if (!res.ok || !data.success) {
        setPdfList([]);
        setPdfListMsg(data.message || "โหลดรายการ PDF ไม่สำเร็จ");
        return;
      }

      const nextItems = (data.items || []).map((item) => ({
        ...item,
        url: toAbsoluteUrl(item.url),
      }));
      setPdfList(nextItems);
      if (nextItems.length === 0) setPdfListMsg("ยังไม่มีรายการ PDF");
    } catch (err) {
      console.error(err);
      setPdfList([]);
      setPdfListMsg("เชื่อมต่อ server ไม่ได้");
    } finally {
      setPdfListLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncementsByDate(todayStr);
    loadPdfList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayStr]);

  const updateIssueDate = async (id) => {
    const newDate = editMap[id];
    if (!newDate) {
      alert("กรุณาเลือกวันที่ใหม่");
      return;
    }

    try {
      setBusyId(id);
      setItemsMsg("");

      const res = await fetch(`${API_BASE}/api/announcement/${id}/issue-date`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ issueDate: newDate }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setItemsMsg(data.message || "อัปเดตวันที่ไม่สำเร็จ");
        return;
      }

      // the item stays in this list only when its new date is still the day being viewed
      setItems((prev) => (newDate === manageDate
        ? prev.map((item) => (item.id === id ? { ...item, issueDate: newDate } : item))
        : prev.filter((item) => item.id !== id)));
      setItemsMsg("อัปเดตวันที่สำเร็จ");
    } catch (err) {
      console.error(err);
      setItemsMsg("เชื่อมต่อ server ไม่ได้");
    } finally {
      setBusyId(null);
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!id) return;
    const ok = window.confirm("ยืนยันลบประกาศนี้?");
    if (!ok) return;

    try {
      setBusyId(id);
      setItemsMsg("");

      const res = await fetch(`${API_BASE}/api/announcement/${id}`, { method: "DELETE" });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setItemsMsg(data.message || "ลบไม่สำเร็จ");
        return;
      }

      setItems((prev) => prev.filter((item) => item.id !== id));
      setItemsMsg("ลบสำเร็จ");
    } catch (err) {
      console.error(err);
      setItemsMsg("เชื่อมต่อ server ไม่ได้");
    } finally {
      setBusyId(null);
    }
  };

  const deletePdf = async (pdf) => {
    if (!pdf?.id) return;
    const ok = window.confirm(`ยืนยันลบไฟล์ PDF ฉบับวันที่ ${formatDate(pdf.date)}?\n${pdf.fileName || ""}`);
    if (!ok) return;

    try {
      setBusyId(`pdf-${pdf.id}`);
      setPdfListMsg("");

      const res = await fetch(`${API_BASE}/api/daily-pdf/${encodeURIComponent(pdf.id)}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        setPdfListMsg(data.message || (res.status === 401 ? "กรุณาเข้าสู่ระบบใหม่" : "ลบ PDF ไม่สำเร็จ"));
        return;
      }

      setPdfList((prev) => prev.filter((p) => p.id !== pdf.id));
      if (generatedPdfUrl && generatedPdfUrl === pdf.url) setGeneratedPdfUrl("");
      setPdfListMsg("ลบ PDF สำเร็จ");
    } catch (err) {
      console.error(err);
      setPdfListMsg("เชื่อมต่อ server ไม่ได้");
    } finally {
      setBusyId(null);
    }
  };

  const generatePdf = async () => {
    if (!manageDate) {
      alert("กรุณาเลือกวันที่ก่อน");
      return;
    }

    try {
      setPdfLoading(true);
      setPdfMsg("กำลังสร้าง PDF...");
      setGeneratedPdfUrl("");

      const res = await fetch(`${API_BASE}/api/daily-pdf/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: manageDate }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setPdfMsg(data.message || "สร้าง PDF ไม่สำเร็จ");
        return;
      }

      setGeneratedPdfUrl(toAbsoluteUrl(data.pdfUrl));
      setPdfMsg("สร้าง PDF สำเร็จ");
      await loadPdfList();
    } catch (err) {
      console.error(err);
      setPdfMsg("เชื่อมต่อ server ไม่ได้");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="announce-form">
      <div className="af-category">
        <div className="af-cat-title">จัดการประกาศรายวัน</div>

        <div className="manage-toolbar">
          <div className="manage-field">
            <label className="form-label">เลือกวันที่</label>
            <DateInput
              className="form-input"
              value={manageDate}
              onChange={(event) => setManageDate(event.target.value)}
            />
          </div>

          <div className="manage-actions">
            <button
              type="button"
              className="btn"
              onClick={() => loadAnnouncementsByDate(manageDate)}
              disabled={itemsLoading}
            >
              {itemsLoading ? "กำลังโหลด..." : "ดูรายการ"}
            </button>
            <button type="button" className="btn primary" onClick={generatePdf} disabled={pdfLoading}>
              {pdfLoading ? "กำลังสร้าง..." : "สร้าง PDF"}
            </button>
          </div>
        </div>

        {itemsMsg && <div className="status-text">{itemsMsg}</div>}
        {pdfMsg && <div className="status-text">{pdfMsg}</div>}

        {generatedPdfUrl && (
          <div className="pdf-box">
            <div className="pdf-row">
              <span className="pdf-label">ไฟล์ PDF:</span>
              <a className="pdf-link" href={generatedPdfUrl} target="_blank" rel="noreferrer">
                {generatedPdfUrl}
              </a>
            </div>
            <iframe title="pdf-preview" className="pdf-frame" src={generatedPdfUrl} />
          </div>
        )}

        {!itemsLoading && items.length > 0 && <PageSummary paging={itemPaging} />}
        <div className="manage-grid">
          {itemsLoading ? (
            <div className="empty-box">กำลังโหลดรายการ...</div>
          ) : items.length === 0 ? (
            <div className="empty-box">ไม่พบประกาศของวันที่เลือก</div>
          ) : (
            itemPaging.shown.map((item) => (
              <div className="ann-card" key={item.id}>
                <div className="ann-thumb">
                  {/\.pdf($|\?)/i.test(item.imageUrl || "") ? (
                    <a className="btn" href={item.imageUrl} target="_blank" rel="noreferrer">เปิดไฟล์ PDF ↗</a>
                  ) : (
                    <img src={item.imageUrl} alt={`announcement-${item.id}`} />
                  )}
                </div>

                <div className="ann-meta">
                  <div className="ann-id">ID: {item.id}</div>
                  <div className="ann-small">Uploaded: {formatDateTime(item.uploadedAt) || "-"}</div>

                  <div className="ann-edit">
                    <label className="form-label">IssueDate</label>
                    <DateInput
                      className="form-input"
                      value={editMap[item.id] || item.issueDate || ""}
                      onChange={(event) =>
                        setEditMap((prev) => ({
                          ...prev,
                          [item.id]: event.target.value,
                        }))
                      }
                    />

                    <div className="ann-btns">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => updateIssueDate(item.id)}
                        disabled={busyId === item.id}
                      >
                        {busyId === item.id ? "กำลังบันทึก..." : "บันทึก"}
                      </button>
                      <button
                        type="button"
                        className="btn danger"
                        onClick={() => deleteAnnouncement(item.id)}
                        disabled={busyId === item.id}
                      >
                        {busyId === item.id ? "กำลังลบ..." : "ลบ"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {!itemsLoading && <PageNav paging={itemPaging} />}
      </div>

      <div className="af-category">
        <div className="af-cat-title">รายการ PDF ที่เคยสร้าง</div>

        <div className="manage-actions" style={{ marginBottom: 10 }}>
          <button type="button" className="btn" onClick={loadPdfList} disabled={pdfListLoading}>
            {pdfListLoading ? "กำลังโหลด..." : "รีเฟรช"}
          </button>
        </div>

        {pdfListMsg && <div className="status-text">{pdfListMsg}</div>}

        {pdfList.length === 0 ? (
          <div className="empty-box">ยังไม่มี PDF</div>
        ) : (
          <>
          <PageSummary paging={pdfPaging} unit="ไฟล์" />
          <div className="pdf-list">
            {pdfPaging.shown.map((pdf) => (
              <div className="pdf-item" key={pdf.id || pdf.url || pdf.fileName}>
                <div className="pdf-item-left">
                  <div className="pdf-date">{formatDate(pdf.date)}</div>
                  <div className="pdf-filename">{pdf.fileName}</div>
                </div>
                <div className="pdf-item-right">
                  <a className="btn" href={pdf.url} target="_blank" rel="noreferrer">
                    เปิด
                  </a>
                  <button
                    type="button"
                    className="btn danger"
                    onClick={() => deletePdf(pdf)}
                    disabled={busyId === `pdf-${pdf.id}`}
                  >
                    {busyId === `pdf-${pdf.id}` ? "กำลังลบ..." : "ลบ"}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <PageNav paging={pdfPaging} />
          </>
        )}
      </div>
    </div>
  );
}

export default AdminDailyAnnouncementManager;
